import TrashIcon from "~icons/fa6-regular/trash-can";
import Button from "@/lib/Button";
import { useState, ChangeEvent, Dispatch, SetStateAction } from "react";
import { EditLineItem, EditLineItems, LineItem, LocalLineItems } from "./types";

export default function EditLineItemsForm({
  selectedLineItemKeys,
  setSelectedLineItemKeys,
  updateLineItems,
  lineItems,
}: {
  selectedLineItemKeys: string[];
  setSelectedLineItemKeys: Dispatch<SetStateAction<string[]>>;
  updateLineItems: (lineItems: LocalLineItems[], save?: boolean) => void;
  lineItems: LocalLineItems[];
}) {
  const [editLineItems, setEditLineItems] =
    useState<EditLineItems>(defaultEditLineItems);

  return (
    <div className="p-4 flex flex-col gap-2">
      <div className="text-sm font-bold">
        Update {selectedLineItemKeys.length} Selected
      </div>
      <table className="w-full rounded-sm bg-white border-collapse">
        <thead className="font-bold text-sm bg-white">
          <tr>
            <th scope="col" />
            <th scope="col" className="text-left p-2">
              Lead Time
            </th>
            <th scope="col" className="text-left p-2">
              Adjust Price
            </th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody className="text-sm">
          <tr>
            <td className="text-left p-2">Modify</td>
            <td className="text-left p-2">
              <input
                value={editLineItems.modify.lead_time}
                onChange={handleChangeModifyLeadTime}
                className="border-b focus:outline-hidden focus:border-blue-600"
              />
            </td>
            <td className="text-left p-2">
              <div className="flex gap-1">
                <input
                  placeholder="0"
                  value={editLineItems.modify.adjustPrice}
                  onChange={handleChangeModifyAdjustPrice}
                  className="w-[50px] border-b focus:outline-hidden focus:border-blue-600"
                />
                <div>%</div>
              </div>
            </td>
          </tr>
          {editLineItems.add.map((_, i) => (
            <tr key={i}>
              <td className="text-left p-2">Add Option {i + 1}</td>
              <td className="text-left p-2">
                <input
                  value={editLineItems.add[i].lead_time}
                  onChange={(e) =>
                    handleChangeOptionLeadTime(i, e.target.value)
                  }
                  className="border-b focus:outline-hidden focus:border-blue-600"
                />
              </td>
              <td className="text-left p-2">
                <div className="flex gap-1">
                  <input
                    placeholder="0"
                    value={editLineItems.add[i].adjustPrice}
                    onChange={(e) =>
                      handleChangeOptionAdjustPrice(i, e.target.value)
                    }
                    className="w-[50px] border-b focus:outline-hidden focus:border-blue-600"
                  />
                  <div>%</div>
                </div>
              </td>
              <td>
                <TrashIcon
                  className="cursor-pointer text-slate-400 hover:text-slate-600 size-3"
                  onClick={() => handleClickDeleteOption(i)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button onClick={handleClickAddOption}>Add Option</Button>
      <div className="flex justify-end gap-2">
        <Button onClick={handleClickCancel}>Cancel</Button>
        <Button
          primary
          onClick={handleClickApply}
          disabled={!anyValidEditLineItem(editLineItems)}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  function handleChangeModifyLeadTime(e: ChangeEvent<HTMLInputElement>) {
    setEditLineItems({
      ...editLineItems,
      modify: {
        ...editLineItems.modify,
        lead_time: e.target.value,
      },
    });
  }

  function handleChangeModifyAdjustPrice(e: ChangeEvent<HTMLInputElement>) {
    setEditLineItems({
      ...editLineItems,
      modify: {
        ...editLineItems.modify,
        adjustPrice: removeBadCharsAndDetectSign(e.target.value),
      },
    });

    function removeBadCharsAndDetectSign(input: string) {
      const signs = input.replace(/[^+|-]/g, "");
      const negative = signs.charAt(signs.length - 1) === "-";
      return `${negative ? "-" : "+"}${input.replace(/\D/g, "")}`;
    }
  }

  function handleChangeOptionLeadTime(optionIndex: number, value: string) {
    setEditLineItems({
      ...editLineItems,
      add: editLineItems.add.map((a, j) =>
        optionIndex === j ? { ...a, lead_time: value } : a,
      ),
    });
  }

  function handleChangeOptionAdjustPrice(optionIndex: number, value: string) {
    setEditLineItems({
      ...editLineItems,
      add: editLineItems.add.map((a, j) =>
        optionIndex === j ? { ...a, adjustPrice: value } : a,
      ),
    });
  }

  function handleClickDeleteOption(optionIndex: number) {
    setEditLineItems({
      ...editLineItems,
      add: editLineItems.add.filter((_, j) => j !== optionIndex),
    });
  }

  function handleClickAddOption() {
    setEditLineItems({
      ...editLineItems,
      add: [...editLineItems.add, { lead_time: "", adjustPrice: "" }],
    });
  }

  function handleClickCancel() {
    setSelectedLineItemKeys([]);
    setEditLineItems(defaultEditLineItems());
  }

  function handleClickApply() {
    const newLineItems = updateParts(
      lineItems,
      selectedLineItemKeys,
      editLineItems,
    );
    updateLineItems(newLineItems);
    setSelectedLineItemKeys([]);
    setEditLineItems(defaultEditLineItems());
    window.setTimeout(
      () => updateLineItems(clearLineItemsHighlight(newLineItems), false),
      3 * 1000,
    );
  }
}

function anyValidEditLineItem(editLineItems: EditLineItems) {
  let result = validEditLineItem(editLineItems.modify);
  editLineItems.add.forEach((e) => (result = result || validEditLineItem(e)));
  return result;
}

function clearLineItemsHighlight(parts: LocalLineItems[]): LocalLineItems[] {
  return parts.map((p) => ({
    ...p,
    item_lines: p.item_lines.map((l) => ({ ...l, highlight: false })),
  }));
}

function updateParts(
  parts: LocalLineItems[],
  selectedLineItemKeys: string[],
  editLineItems: EditLineItems,
): LocalLineItems[] {
  return parts.map((p) => ({
    ...p,
    item_lines: updateSelectedLineItems(
      p.item_lines,
      selectedLineItemKeys,
      editLineItems,
    ),
  }));
}

function updateSelectedLineItems(
  lineItems: LineItem[],
  selectedLineItemKeys: string[],
  editLineItems: EditLineItems,
) {
  const { modify, add } = editLineItems;
  const result: LineItem[] = [];
  lineItems.forEach((l) => {
    if (selectedLineItemKeys.includes(l.key)) {
      result.push(updateLineItem(l, modify));
      add.forEach((a, i) => {
        if (validEditLineItem(a)) {
          result.push(updateLineItem(l, a, `${l.key}-${i}`));
        }
      });
    } else {
      result.push(l);
    }
  });
  return result;
}

function updateLineItem(
  lineItem: LineItem,
  editLineItem: EditLineItem,
  key: string = lineItem.key,
): LineItem {
  return {
    ...lineItem,
    key,
    lead_time: editLineItem.lead_time || lineItem.lead_time,
    unitPrice:
      ((parseFloat(editLineItem.adjustPrice) || 0) / 100 + 1) *
      lineItem.unitPrice,
    highlight: validEditLineItem(editLineItem),
  };
}

function validEditLineItem(editLineItem: EditLineItem) {
  return !!editLineItem.adjustPrice || !!editLineItem.lead_time;
}

function defaultEditLineItems(): EditLineItems {
  return {
    modify: defaultEditLineItem(),
    add: [defaultEditLineItem()],
  };
}

function defaultEditLineItem(): EditLineItem {
  return { lead_time: "", adjustPrice: "" };
}
